import { TuiDocument, Guide, DesignTokens, emptyTokens } from '../core/Document';
import { Layer } from '../core/Layer';
import { Cell, ColorMode, TRANSPARENT } from '../core/types';
import type { VariableDef, VariableType, VariableValues } from '../runtime/Variables';
import { CURRENT_VERSION, migrateProject } from './Migration';

/**
 * The `.tui.json` project format.
 *
 * ANSI is an export target, not the source of truth, so this is what actually
 * gets saved: layers, variables, tokens and animation metadata. Cells are
 * run-length encoded row-major, which is where the size win is — terminal art
 * is mostly runs of identical cells.
 *
 * Everything read back in is treated as untrusted: shapes are validated,
 * numbers clamped, unknown fields dropped, and nothing in a project file can
 * cause code to run.
 */

export const PROJECT_FORMAT = 'tui-project';

/** [repeatCount, char, fg, bg, attrs] */
export type CellRun = [number, string, number, number, number];

export interface SerializedLayer {
  id: string;
  name: string;
  visible: boolean;
  locked: boolean;
  runs: CellRun[];
}

export interface SerializedAnimation {
  id: string;
  name: string;
  fps: number;
  loop: boolean;
  /** Layer ids shown on each frame. */
  frames: Array<{ layers: string[]; durationMs?: number }>;
}

export interface SerializedComponent {
  id: string;
  name: string;
  width: number;
  height: number;
  runs: CellRun[];
}

export interface SerializedProject {
  format: typeof PROJECT_FORMAT;
  version: number;
  name: string;
  width: number;
  height: number;
  colorMode: ColorMode;
  background: number;
  activeLayerId: string;
  layers: SerializedLayer[];
  variables: VariableDef[];
  previewValues: VariableValues;
  tokens: DesignTokens;
  guides: Guide[];
  components: SerializedComponent[];
  animations: SerializedAnimation[];
  meta: {
    app: string;
    created: string;
    modified: string;
  };
}

export const MAX_DIMENSION = 2000;

function encodeRuns(read: (i: number) => Cell, length: number): CellRun[] {
  const runs: CellRun[] = [];
  let run: CellRun | null = null;
  for (let i = 0; i < length; i++) {
    const c = read(i);
    if (
      run !== null &&
      run[1] === c.char &&
      run[2] === c.fg &&
      run[3] === c.bg &&
      run[4] === c.attrs
    ) {
      run[0] += 1;
      continue;
    }
    run = [1, c.char, c.fg, c.bg, c.attrs];
    runs.push(run);
  }
  return runs;
}

function decodeRuns(runs: CellRun[], write: (i: number, cell: Cell) => void, limit: number): void {
  let i = 0;
  for (const run of runs) {
    if (!Array.isArray(run) || run.length < 5) continue;
    const count = Math.max(0, Math.min(limit - i, Math.trunc(Number(run[0]) || 0)));
    const cell: Cell = {
      char: typeof run[1] === 'string' ? run[1].slice(0, 8) : '',
      fg: safeColor(run[2]),
      bg: safeColor(run[3]),
      attrs: Math.max(0, Math.min(0xffff, Math.trunc(Number(run[4]) || 0))),
    };
    for (let n = 0; n < count; n++) write(i + n, cell);
    i += count;
    if (i >= limit) break;
  }
}

function safeColor(v: unknown): number {
  const n = Math.trunc(Number(v));
  if (!Number.isFinite(n)) return TRANSPARENT;
  if (n < 0) return TRANSPARENT;
  return n & 0xffffff;
}

export interface SerializeExtras {
  components?: SerializedComponent[];
  animations?: SerializedAnimation[];
  created?: string;
}

export function serializeDocument(doc: TuiDocument, extras: SerializeExtras = {}): SerializedProject {
  const now = new Date().toISOString();
  return {
    format: PROJECT_FORMAT,
    version: CURRENT_VERSION,
    name: doc.name,
    width: doc.width,
    height: doc.height,
    colorMode: doc.colorMode,
    background: doc.background,
    activeLayerId: doc.activeLayerId,
    layers: doc.layers.map((layer) => ({
      id: layer.id,
      name: layer.name,
      visible: layer.visible,
      locked: layer.locked,
      runs: encodeRuns((i) => layer.grid.getAt(i), layer.grid.length),
    })),
    variables: doc.variables.map((v) => ({ ...v })),
    previewValues: { ...doc.previewValues },
    tokens: {
      colors: { ...doc.tokens.colors },
      characters: { ...doc.tokens.characters },
      numbers: { ...doc.tokens.numbers },
    },
    guides: doc.guides.map((g) => ({ ...g })),
    components: extras.components ?? [],
    animations: extras.animations ?? [],
    meta: {
      app: 'Pixansi',
      created: extras.created ?? now,
      modified: now,
    },
  };
}

export function toJson(doc: TuiDocument, extras: SerializeExtras = {}, pretty = false): string {
  const data = serializeDocument(doc, extras);
  return pretty ? JSON.stringify(data, null, 2) : JSON.stringify(data);
}

export class ProjectValidationError extends Error {}

export interface LoadResult {
  document: TuiDocument;
  components: SerializedComponent[];
  animations: SerializedAnimation[];
  warnings: string[];
  created: string;
}

const COLOR_MODES: ColorMode[] = ['ansi16', 'ansi256', 'truecolor'];
const VARIABLE_TYPES: VariableType[] = [
  'string',
  'integer',
  'float',
  'boolean',
  'percentage',
  'duration',
  'timestamp',
  'array',
  'computed',
];

function clampInt(v: unknown, min: number, max: number, fallback: number): number {
  const n = Math.trunc(Number(v));
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, n));
}

function safeString(v: unknown, max = 200, fallback = ''): string {
  return typeof v === 'string' ? v.slice(0, max) : fallback;
}

/** Parses and validates a project file. Never executes anything it reads. */
export function fromJson(text: string): LoadResult {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch (e) {
    throw new ProjectValidationError(`Not valid JSON: ${(e as Error).message}`);
  }
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    throw new ProjectValidationError('Project file must be a JSON object.');
  }

  const warnings: string[] = [];
  const obj = raw as Record<string, unknown>;
  if (obj.format !== PROJECT_FORMAT) {
    throw new ProjectValidationError(
      `Unrecognised project format "${String(obj.format)}" — expected "${PROJECT_FORMAT}".`,
    );
  }

  const migrated = migrateProject(obj, warnings);

  const width = clampInt(migrated.width, 1, MAX_DIMENSION, 80);
  const height = clampInt(migrated.height, 1, MAX_DIMENSION, 24);
  const colorMode = COLOR_MODES.includes(migrated.colorMode as ColorMode)
    ? (migrated.colorMode as ColorMode)
    : 'truecolor';

  const doc = new TuiDocument({
    name: safeString(migrated.name, 120, 'Untitled'),
    width,
    height,
    colorMode,
    background: safeColor(migrated.background),
  });
  doc.layers = [];

  const layersRaw = Array.isArray(migrated.layers) ? migrated.layers : [];
  for (const entry of layersRaw) {
    if (typeof entry !== 'object' || entry === null) continue;
    const l = entry as Record<string, unknown>;
    const layer = new Layer(width, height, {
      id: safeString(l.id, 64) || undefined,
      name: safeString(l.name, 120, 'Layer'),
      visible: l.visible !== false,
      locked: l.locked === true,
    });
    if (Array.isArray(l.runs)) {
      decodeRuns(l.runs as CellRun[], (i, cell) => layer.grid.setAt(i, cell), layer.grid.length);
    }
    doc.layers.push(layer);
  }
  if (doc.layers.length === 0) {
    doc.layers.push(new Layer(width, height, { name: 'Background' }));
    warnings.push('Project contained no layers; an empty background layer was created.');
  }
  const activeId = safeString(migrated.activeLayerId, 64);
  doc.activeLayerId = doc.layers.some((l) => l.id === activeId) ? activeId : doc.layers[0].id;

  doc.variables = sanitizeVariables(migrated.variables, warnings);
  doc.previewValues = sanitizeValues(migrated.previewValues);
  doc.tokens = sanitizeTokens(migrated.tokens);
  doc.guides = sanitizeGuides(migrated.guides, width, height);

  const components = sanitizeComponents(migrated.components, warnings);
  const animations = sanitizeAnimations(migrated.animations);
  const created = safeString(
    (migrated.meta as Record<string, unknown> | undefined)?.created,
    40,
    new Date().toISOString(),
  );

  return { document: doc, components, animations, warnings, created };
}

function sanitizeVariables(raw: unknown, warnings: string[]): VariableDef[] {
  if (!Array.isArray(raw)) return [];
  const out: VariableDef[] = [];
  const seen = new Set<string>();
  for (const entry of raw) {
    if (typeof entry !== 'object' || entry === null) continue;
    const v = entry as Record<string, unknown>;
    const name = safeString(v.name, 64);
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(name)) {
      if (name) warnings.push(`Variable "${name}" has an invalid name and was dropped.`);
      continue;
    }
    if (seen.has(name)) continue;
    seen.add(name);
    const type = VARIABLE_TYPES.includes(v.type as VariableType) ? (v.type as VariableType) : 'string';
    out.push({
      name,
      type,
      description: safeString(v.description, 240) || undefined,
      defaultValue: sanitizeScalar(v.defaultValue),
      previewValue: sanitizeScalar(v.previewValue),
      fallback: sanitizeScalar(v.fallback),
      expression: safeString(v.expression, 500) || undefined,
      width: v.width === undefined ? undefined : clampInt(v.width, 0, 500, 0),
      align: v.align === 'right' || v.align === 'center' ? v.align : 'left',
      padChar: safeString(v.padChar, 1) || undefined,
      format: safeString(v.format, 200) || undefined,
      colorRules: Array.isArray(v.colorRules)
        ? (v.colorRules as unknown[])
            .filter((r): r is Record<string, unknown> => typeof r === 'object' && r !== null)
            .slice(0, 32)
            .map((r) => ({
              min: r.min === undefined ? undefined : Number(r.min),
              max: r.max === undefined ? undefined : Number(r.max),
              equals: r.equals === undefined ? undefined : safeString(r.equals, 64),
              color: safeString(r.color, 9, '#ffffff'),
            }))
        : undefined,
    });
  }
  return out;
}

function sanitizeScalar(v: unknown): string | number | boolean | null | undefined {
  if (v === undefined) return undefined;
  if (v === null) return null;
  const t = typeof v;
  if (t === 'string') return (v as string).slice(0, 500);
  if (t === 'number') return Number.isFinite(v as number) ? (v as number) : 0;
  if (t === 'boolean') return v as boolean;
  if (Array.isArray(v)) return v.slice(0, 100).join(', ');
  return undefined;
}

function sanitizeValues(raw: unknown): VariableValues {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return {};
  const out: VariableValues = {};
  let n = 0;
  for (const [k, v] of Object.entries(raw)) {
    if (n++ > 500) break;
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(k)) continue;
    const s = sanitizeScalar(v);
    if (s !== undefined) out[k] = s;
  }
  return out;
}

function sanitizeTokens(raw: unknown): DesignTokens {
  const out = emptyTokens();
  if (typeof raw !== 'object' || raw === null) return out;
  const t = raw as Record<string, unknown>;
  const copyStrings = (src: unknown, dest: Record<string, string>) => {
    if (typeof src !== 'object' || src === null) return;
    for (const [k, v] of Object.entries(src)) {
      if (typeof v === 'string' && /^[A-Za-z0-9_.-]{1,64}$/.test(k)) dest[k] = v.slice(0, 64);
    }
  };
  copyStrings(t.colors, out.colors);
  copyStrings(t.characters, out.characters);
  if (typeof t.numbers === 'object' && t.numbers !== null) {
    for (const [k, v] of Object.entries(t.numbers)) {
      if (typeof v === 'number' && Number.isFinite(v)) out.numbers[k.slice(0, 64)] = v;
    }
  }
  return out;
}

function sanitizeGuides(raw: unknown, width: number, height: number): Guide[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((g): g is Record<string, unknown> => typeof g === 'object' && g !== null)
    .slice(0, 200)
    .map((g) => ({
      axis: g.axis === 'y' ? ('y' as const) : ('x' as const),
      position: clampInt(g.position, 0, g.axis === 'y' ? height : width, 0),
    }));
}

function sanitizeComponents(raw: unknown, warnings: string[]): SerializedComponent[] {
  if (!Array.isArray(raw)) return [];
  const out: SerializedComponent[] = [];
  for (const entry of raw.slice(0, 500)) {
    if (typeof entry !== 'object' || entry === null) continue;
    const c = entry as Record<string, unknown>;
    const width = clampInt(c.width, 1, MAX_DIMENSION, 1);
    const height = clampInt(c.height, 1, MAX_DIMENSION, 1);
    if (!Array.isArray(c.runs)) {
      warnings.push(`Component "${safeString(c.name, 64)}" had no cell data and was dropped.`);
      continue;
    }
    out.push({
      id: safeString(c.id, 64) || `component-${out.length}`,
      name: safeString(c.name, 120, 'Component'),
      width,
      height,
      runs: c.runs as CellRun[],
    });
  }
  return out;
}

function sanitizeAnimations(raw: unknown): SerializedAnimation[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((a): a is Record<string, unknown> => typeof a === 'object' && a !== null)
    .slice(0, 100)
    .map((a, i) => ({
      id: safeString(a.id, 64) || `anim-${i}`,
      name: safeString(a.name, 120, 'Animation'),
      fps: clampInt(a.fps, 1, 60, 10),
      loop: a.loop !== false,
      frames: Array.isArray(a.frames)
        ? (a.frames as unknown[])
            .filter((f): f is Record<string, unknown> => typeof f === 'object' && f !== null)
            .slice(0, 1000)
            .map((f) => ({
              layers: Array.isArray(f.layers)
                ? (f.layers as unknown[]).filter((l): l is string => typeof l === 'string').slice(0, 200)
                : [],
              durationMs: f.durationMs === undefined ? undefined : clampInt(f.durationMs, 1, 60000, 100),
            }))
        : [],
    }));
}

/** Rebuilds a component's cells for stamping. */
export function componentCells(component: SerializedComponent): Cell[] {
  const cells = new Array<Cell>(component.width * component.height).fill({
    char: '',
    fg: TRANSPARENT,
    bg: TRANSPARENT,
    attrs: 0,
  });
  decodeRuns(component.runs, (i, cell) => {
    cells[i] = cell;
  }, cells.length);
  return cells;
}

export function encodeComponentRuns(read: (i: number) => Cell, length: number): CellRun[] {
  return encodeRuns(read, length);
}
